import { Button } from '@heidi/design-system/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { cn, useOverlayTriggerTooltip } from '@heidi/utils';
import { ChevronDown, Laptop } from 'lucide-react';
import { useState, type JSX } from 'react';
import { defineMessages, useIntl } from 'react-intl';

const messages = defineMessages({
  triggerAriaLabel: {
    defaultMessage: 'Computer',
    id: '9nGTKQXJm/',
    description: 'Aria label for the desktop-target picker in the composer',
  },
  triggerTooltip: {
    defaultMessage: 'Computer Heidi should use',
    id: 'fc+PjrGCth',
    description: 'Tooltip for the desktop-target picker',
  },
  noComputer: {
    defaultMessage: 'No computer',
    id: 'rL4XU3fEQM',
    description: 'Desktop-target picker option that sends no target_id',
  },
  thisComputer: {
    defaultMessage: 'This computer',
    id: 'qlCbPqd1iB',
    description: 'Badge on the local desktop in the target picker',
  },
  unavailable: {
    defaultMessage: 'Offline',
    id: 'BmdKiwILBE',
    description: 'Badge on an unavailable desktop target',
  },
  computerOffline: {
    defaultMessage: 'Computer offline',
    id: 'EsCHHA7l2u',
    description:
      'Desktop-target picker trigger when the saved computer is no longer registered',
  },
  pickAnother: {
    defaultMessage: 'That computer is offline. Pick another.',
    id: '7z3yOs0Kmv',
    description: 'Helper when the selected desktop target cannot accept work',
  },
  yourResident: {
    defaultMessage: 'Your Resident',
    id: 'dP6ql4blcK',
    description: 'Picker entry offering to provision the cloud resident',
  },
  residentSetUp: {
    defaultMessage: 'Set up',
    id: 'CPT3pgX3Ey',
    description: 'Action hint to provision the cloud resident from the picker',
  },
  residentProvisioning: {
    defaultMessage: 'Provisioning…',
    id: 'fu7U53n3vw',
    description: 'Picker entry state while the cloud resident is being provisioned',
  },
});

import { DesktopTargetRosterStatus } from './DesktopTargetRosterStatus';
import {
  NO_COMPUTER_SELECTION,
  selectorValueForSelection,
  type DesktopTargetSelectorProps,
} from './DesktopTargetSelector.types';

export type {
  DesktopTargetSelectorProps,
  DesktopTargetSelectorState,
} from './DesktopTargetSelector.types';

export function DesktopTargetSelector({
  desktopTarget,
  disabled = false,
  className,
}: DesktopTargetSelectorProps): JSX.Element | null {
  const intl = useIntl();
  const [menuOpen, setMenuOpen] = useState(false);
  const { tooltipProps, contentProps } = useOverlayTriggerTooltip();
  const { open: tooltipOpen, onOpenChange: setTooltipOpen } = tooltipProps;
  const {
    visible,
    rosterStatus,
    selection,
    thisComputer,
    live,
    dead,
    residentSetup,
    setSticky,
    retry,
  } = desktopTarget;

  if (!visible) return null;

  if (rosterStatus !== 'ready') {
    return (
      <DesktopTargetRosterStatus
        status={rosterStatus}
        onRetry={retry}
        className={className}
      />
    );
  }

  const hasChoice =
    Boolean(thisComputer) || live.length > 0 || dead.length > 0 || Boolean(residentSetup);
  // Holds the row's height so the composer below never shifts.
  if (!hasChoice) {
    return (
      <span
        aria-hidden
        data-testid="desktop-target-selector-placeholder"
        className={cn('block h-6', className)}
      />
    );
  }

  const selectedValue = selectorValueForSelection(selection);
  const noneLabel = intl.formatMessage(messages.noComputer);

  const triggerLabel =
    selection.kind === NO_COMPUTER_SELECTION
      ? noneLabel
      : selection.kind === 'target'
        ? selection.target.display_name
        : (selection.target?.display_name ??
          intl.formatMessage(messages.computerOffline));

  const helper =
    selection.kind === 'dead'
      ? intl.formatMessage(messages.pickAnother)
      : intl.formatMessage(messages.triggerTooltip);

  return (
    <DropdownMenu
      // Non-modal: a modal menu sends every outside press to <html>, which the
      // compact Work composer treats as a click away, collapsing it under the
      // open menu.
      modal={false}
      open={menuOpen}
      onOpenChange={(next) => {
        if (next && disabled) return;
        setMenuOpen(next);
        if (next) setTooltipOpen(false);
      }}
    >
      <Tooltip open={menuOpen ? false : tooltipOpen} onOpenChange={setTooltipOpen}>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild disabled={disabled}>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disableAutomaticTypographyClasses
              disabled={disabled}
              aria-label={intl.formatMessage(messages.triggerAriaLabel)}
              aria-invalid={selection.kind === 'dead' || undefined}
              data-testid="desktop-target-selector-trigger"
              className={cn(
                'h-6 max-w-44 gap-1 rounded-md px-1.5 text-xs font-normal text-text-tertiary',
                'hover:bg-surface-hover hover:text-text-primary',
                className
              )}
            >
              <Laptop className="size-3.5 shrink-0" aria-hidden />
              <span className="truncate">{triggerLabel}</span>
              <ChevronDown className="size-3 shrink-0" aria-hidden />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>
          {helper}
          <TooltipArrow />
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent
        align="start"
        className="w-72"
        data-testid="desktop-target-selector-menu"
        onCloseAutoFocus={contentProps.onCloseAutoFocus}
      >
        <DropdownMenuRadioGroup
          value={selectedValue}
          onValueChange={(next) => {
            const value = String(next);
            setSticky(value === NO_COMPUTER_SELECTION ? NO_COMPUTER_SELECTION : value);
          }}
        >
          {thisComputer ? (
            <DropdownMenuRadioItem
              value={thisComputer.target_id}
              data-testid="desktop-target-this-computer"
            >
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="text-sm text-text-primary">
                  {thisComputer.display_name}
                </span>
                <span className="text-xs text-text-tertiary">
                  {thisComputer.is_available
                    ? intl.formatMessage(messages.thisComputer)
                    : intl.formatMessage(messages.unavailable)}
                </span>
              </span>
            </DropdownMenuRadioItem>
          ) : null}
          {live.map((target) => (
            <DropdownMenuRadioItem
              key={target.target_id}
              value={target.target_id}
              data-testid={`desktop-target-${target.target_id}`}
            >
              <span className="text-sm text-text-primary">{target.display_name}</span>
            </DropdownMenuRadioItem>
          ))}
          {dead.map((target) => (
            <DropdownMenuRadioItem
              key={target.target_id}
              value={target.target_id}
              data-testid={`desktop-target-${target.target_id}`}
            >
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="text-sm text-text-primary">{target.display_name}</span>
                <span className="text-xs text-text-tertiary">
                  {intl.formatMessage(messages.unavailable)}
                </span>
              </span>
            </DropdownMenuRadioItem>
          ))}
          {residentSetup ? (
            <DropdownMenuItem
              data-testid="desktop-target-resident-setup"
              disabled={residentSetup.provisioning}
              onSelect={() => residentSetup.setUp()}
            >
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="text-sm text-text-primary">
                  {intl.formatMessage(messages.yourResident)}
                </span>
                <span className="text-xs text-text-tertiary">
                  {residentSetup.provisioning
                    ? intl.formatMessage(messages.residentProvisioning)
                    : intl.formatMessage(messages.residentSetUp)}
                </span>
              </span>
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuRadioItem
            value={NO_COMPUTER_SELECTION}
            data-testid="desktop-target-no-computer"
          >
            <span className="text-sm text-text-primary">{noneLabel}</span>
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
